import React, { useState, useMemo } from "react";
import { Calculator, ArrowRight, ArrowLeft, Sparkles, MessageCircle, RotateCcw } from "lucide-react";

const treatments = [
  {
    key: "implants",
    label: "Dental Implants",
    tag: "Missing teeth",
    steps: [
      {
        q: "How many teeth do you want to replace?",
        opts: [
          { label: "1 tooth", value: "1" },
          { label: "2–3 teeth", value: "2-3" },
          { label: "4–6 teeth", value: "4-6" },
          { label: "Full Arch — All-on-4", value: "arch4" },
          { label: "Full Arch — All-on-6", value: "arch6" },
          { label: "Full Mouth — All-on-4 (Both Arches)", value: "full4" },
          { label: "Full Mouth — All-on-6 (Both Arches)", value: "full6" },
        ],
      },
      {
        q: "Which implant system do you prefer?",
        opts: [
          {
            label: "Standard (Israeli) — Adin / Noris / MIS / BioLine / Alpha-Bio",
            value: "standard",
          },
          {
            label: "Value (Korean) — Osstem / Dentium / DIO / Dentis / NeoBiotech / Cowellmedi",
            value: "value",
          },
          {
            label: "Premium — Ankylos / bredent / Implant Swiss / Nobel Biocare / Straumann / Zimmer / BioHorizons",
            value: "premium",
          },
        ],
      },
      {
        q: "Which crown material do you prefer?",
        opts: [
          { label: "Metal Ceramic (PFM)", value: "pfm" },
          { label: "Metal Ceramic (DMLS)", value: "dmls" },
          { label: "Zirconia (Recommended)", value: "zirconia" },
          { label: "E-max (Premium Aesthetic)", value: "emax" },
        ],
      },
    ],
    compute: (a) => {
      const extent = a[0].value;

      const singleTooth = {
        standard: {
          pfm: [24000, 32000],
          dmls: [27000, 36000],
          zirconia: [30000, 42000],
          emax: [33000, 45000],
        },
        value: {
          pfm: [26000, 36000],
          dmls: [29000, 40000],
          zirconia: [33000, 46000],
          emax: [36000, 50000],
        },
        premium: {
          pfm: [35000, 50000],
          dmls: [38000, 54000],
          zirconia: [42000, 60000],
          emax: [45000, 60000],
        },
      };

      if (extent === "arch4") {
        return { low: 180000, high: 350000, unit: "₹" };
      }

      if (extent === "arch6") {
        return { low: 280000, high: 450000, unit: "₹" };
      }

      if (extent === "full4") {
        return { low: 360000, high: 700000, unit: "₹" };
      }

      if (extent === "full6") {
        return { low: 560000, high: 900000, unit: "₹" };
      }

      const implantTier = a[1].value;
      const crown = a[2].value;

      const [unitLow, unitHigh] = singleTooth[implantTier][crown];

      if (extent === "2-3") {
        return {
          low: unitLow * 2,
          high: unitHigh * 3,
          unit: "₹",
        };
      }

      if (extent === "4-6") {
        return {
          low: unitLow * 4,
          high: unitHigh * 6,
          unit: "₹",
        };
      }

      return {
        low: unitLow,
        high: unitHigh,
        unit: "₹",
      };
    },
  },
  {
    key: "aligners",
    label: "Braces / Aligners",
    tag: "Crooked teeth",
    steps: [
      {
        q: "What sort of correction do you need?",
        opts: [
          { label: "Mild – slight crowding, gaps", value: "mild" },
          { label: "Moderate – crooked / mild bite", value: "mod" },
          { label: "Complex – severe crowding / bite", value: "complex" },
        ],
      },
      {
        q: "Which appliance would you prefer?",
        opts: [
          { label: "Metal braces", value: "metal" },
          { label: "Ceramic (tooth-coloured) braces", value: "ceramic" },
          { label: "Invisalign / clear aligners", value: "invis" },
        ],
      },
      {
        q: "Who is the treatment for?",
        opts: [
          { label: "Teenager (13‑18)", value: "teen" },
          { label: "Adult", value: "adult" },
        ],
      },
    ],
    compute: (a) => {
      const sev = a[0].value; const dev = a[1].value;
      let base = { metal: [35000, 55000], ceramic: [55000, 85000], invis: [150000, 220000] }[dev];
      let low = base[0], high = base[1];
      if (sev === "mod") { low += 10000; high += 25000; }
      if (sev === "complex") { low += 25000; high += 60000; if (dev === "invis") high += 100000; }
      return { low, high, unit: "₹" };
    },
  },
  {
    key: "veneers",
    label: "Veneers / Smile Makeover",
    tag: "Cosmetic",
    steps: [
      {
        q: "How many teeth do you want to work on?",
        opts: [
          { label: "1‑2 teeth", value: 2 },
          { label: "4 front teeth", value: 4 },
          { label: "6 front teeth", value: 6 },
          { label: "8‑10 teeth (full smile)", value: 10 },
        ],
      },
      {
        q: "Which veneer material?",
        opts: [
          { label: "Composite bonding", value: 3500 },
          { label: "Ceramic laminates", value: 12000 },
          { label: "E‑max premium veneers", value: 20000 },
        ],
      },
      {
        q: "Add teeth whitening + gum contour?",
        opts: [
          { label: "No, just veneers", value: 0 },
          { label: "Add whitening (₹10k)", value: 10000 },
          { label: "Full package (whitening + gum)", value: 20000 },
        ],
      },
    ],
    compute: (a) => {
      const n = a[0].value; const unit = a[1].value; const extras = a[2].value;
      const low = n * unit + extras;
      const high = Math.round(low * 1.35);
      return { low, high, unit: "₹" };
    },
  },
];

function fmt(n) { return "₹" + n.toLocaleString("en-IN"); }

export default function CostCalculator() {
  const [tKey, setTKey] = useState(null);
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState([]);

  const treatment = useMemo(() => treatments.find((t) => t.key === tKey), [tKey]);
  const done = treatment && step >= treatment.steps.length;
  const result = useMemo(() => (done ? treatment.compute(ans) : null), [done, treatment, ans]);

  const reset = () => { setTKey(null); setStep(0); setAns([]); };

  const fullArchImplantOptions = ["arch4", "arch6", "full4", "full6"];
  const isFullArchImplant =
    treatment?.key === "implants" &&
    fullArchImplantOptions.includes(ans[0]?.value);

  const pick = (opt) => {
    const next = [...ans];
    next[step] = opt;
    setAns(next);

    const shouldSkipToResult =
      treatment?.key === "implants" &&
      step === 0 &&
      fullArchImplantOptions.includes(opt.value);

    setStep(shouldSkipToResult ? treatment.steps.length : step + 1);
  };

  return (
    <section id="calculator" className="py-24 md:py-32 bg-[#f6f7f4] relative overflow-hidden">
      <div className="absolute -top-32 right-0 w-[440px] h-[440px] rounded-full bg-[#14a68e]/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-[1200px] mx-auto px-6 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="section-eyebrow">Instant Cost Estimate</div>
            <h2 className="mt-4 font-serif-display text-[42px] md:text-[62px] leading-[1.05] text-[#0e2b2b]">
              Get a <span className="hero-italic">price idea</span><br />before you call
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[15px] md:text-[16px] text-[#0e2b2b]/70 leading-relaxed">
              A quick estimator for dental implants, aligners and veneers in Lucknow. Final quote is always confirmed in‑clinic — no hidden charges.
            </p>
          </div>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Treatment picker */}
          <div className="lg:col-span-5">
            <div className="grid gap-3">
              {treatments.map((t) => {
                const isActive = t.key === tKey;
                return (
                  <button key={t.key} onClick={() => { setTKey(t.key); setStep(0); setAns([]); }} className={`text-left p-5 rounded-2xl border transition-all duration-300 flex items-center gap-4 ${isActive ? "bg-[#0e2b2b] border-[#0e2b2b] text-white shadow-xl" : "bg-white border-[#0e2b2b]/10 hover:border-[#14a68e]/50"}`}>
                    <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${isActive ? "bg-[#14a68e] text-white" : "bg-[#eaf6f2] text-[#14a68e]"}`}><Calculator size={20} /></span>
                    <div>
                      <div className={`text-[11px] tracking-[0.18em] uppercase font-semibold ${isActive ? "text-[#5eead4]" : "text-[#14a68e]"}`}>{t.tag}</div>
                      <div className="mt-1 font-serif-display text-[20px] leading-tight">{t.label}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Panel */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-[28px] border border-[#0e2b2b]/10 p-7 md:p-9 min-h-[400px] flex flex-col">
              {!treatment && (
                <div className="m-auto text-center">
                  <span className="w-14 h-14 mx-auto rounded-2xl bg-[#eaf6f2] text-[#14a68e] flex items-center justify-center"><Sparkles size={24} /></span>
                  <div className="mt-5 font-serif-display text-[26px] text-[#0e2b2b]">Pick a treatment to begin</div>
                  <p className="mt-2 text-[14px] text-[#0e2b2b]/60">Answer a few short questions to get an instant price range.</p>
                </div>
              )}

              {treatment && !done && (
                <div>
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#14a68e] font-semibold">Question {step + 1} of {treatment.steps.length}</div>
                    <button onClick={reset} className="text-[12px] text-[#0e2b2b]/60 hover:text-[#0e2b2b] inline-flex items-center gap-1"><RotateCcw size={12} /> Restart</button>
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-[#0e2b2b]/10 overflow-hidden">
                    <div className="h-full bg-[#14a68e] transition-all" style={{ width: `${((step) / treatment.steps.length) * 100}%` }} />
                  </div>
                  <h3 className="mt-6 font-serif-display text-[26px] md:text-[30px] text-[#0e2b2b] leading-tight">{treatment.steps[step].q}</h3>
                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    {treatment.steps[step].opts.map((o) => (
                      <button key={o.label} onClick={() => pick(o)} className="text-left px-5 py-4 rounded-2xl border border-[#0e2b2b]/10 hover:border-[#14a68e] hover:bg-[#eaf6f2] transition flex items-center justify-between gap-3 group">
                        <span className="text-[14.5px] text-[#0e2b2b]">{o.label}</span>
                        <span className="w-8 h-8 rounded-full bg-[#f6f7f4] group-hover:bg-[#14a68e] group-hover:text-white text-[#14a68e] flex items-center justify-center transition"><ArrowRight size={14} /></span>
                      </button>
                    ))}
                  </div>
                  {step > 0 && (
                    <button onClick={() => setStep(step - 1)} className="mt-6 inline-flex items-center gap-2 text-[13px] text-[#0e2b2b]/70 hover:text-[#14a68e]"><ArrowLeft size={14} /> Back</button>
                  )}
                </div>
              )}

              {done && result && (
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#14a68e] font-semibold">Estimated price range</div>
                  <div className="mt-3 font-serif-display text-[42px] md:text-[54px] text-[#0e2b2b] leading-none">
                    {fmt(result.low)} <span className="text-[#14a68e]">–</span> {fmt(result.high)}
                  </div>
                  <p className="mt-4 text-[14px] text-[#0e2b2b]/70 leading-relaxed">This is an indicative range for <b>{treatment.label}</b> in Lucknow (2026). Final quote is written on paper after a free consultation and depends on X‑rays and clinical exam.</p>

                  {isFullArchImplant && (
                    <p className="mt-3 text-[13px] text-[#0e2b2b]/60 leading-relaxed">
                      For full-arch and full-mouth implant treatment, the final cost varies with the implant system, prosthesis/material selected, bone requirements and the individual clinical plan.
                    </p>
                  )}

                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <a href="#contact" className="btn-primary justify-center">Book Free Consult <ArrowRight size={16} /></a>
                    <a href="https://wa.me/918601563494" target="_blank" rel="noreferrer" className="btn-ghost justify-center"><MessageCircle size={16} /> WhatsApp</a>
                  </div>
                  <button onClick={reset} className="mt-4 text-[13px] text-[#0e2b2b]/60 hover:text-[#0e2b2b] inline-flex items-center gap-1"><RotateCcw size={12} /> Start again</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
